
import React, { useState } from 'react';
import { format, subYears } from 'date-fns';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useIsMobile } from '@/hooks/use-mobile';

// Import our newly created components
import { RetentionFilters } from './retention/RetentionFilters';
import { DryRunResults } from './retention/DryRunResults';
import { RetentionActions } from './retention/RetentionActions';
import { useRetentionOperations } from '@/hooks/useRetentionOperations';
import { useAdminNotifications } from '@/hooks/useAdminNotifications';

interface RetentionExecutorProps {
  stateFilter?: string;
  courseTypeFilter?: string;
}

export function RetentionExecutor({ stateFilter, courseTypeFilter }: RetentionExecutorProps) {
  const [state, setState] = useState(stateFilter || '');
  const [courseType, setCourseType] = useState(courseTypeFilter || '');
  const [retentionDate, setRetentionDate] = useState(format(subYears(new Date(), 5), 'yyyy-MM-dd'));
  const isMobile = useIsMobile();
  
  const { sendRetentionNotification } = useAdminNotifications();
  
  const {
    dryRunResults,
    dryRunMutation,
    executeMutation,
    verifyMutation
  } = useRetentionOperations();

  const handleDryRunCompleted = (results: any) => {
    // Send notification when dry run completes with the count of affected records
    if (results && results.affectedRecords) {
      sendRetentionNotification(
        "Retention Dry Run Completed",
        `Retention dry run completed with ${results.affectedRecords} records that would be affected. State: ${state || 'All'}, Course Type: ${courseType || 'All'}.`,
        {
          affectedRecords: results.affectedRecords,
          state: state || 'All',
          courseType: courseType || 'All',
          retentionDate: retentionDate,
          operation: 'dry-run'
        }
      );
    }
  };

  // Handle execution completion
  const handleExecutionCompleted = (results: any) => {
    // Send notification when execution completes
    if (results) {
      sendRetentionNotification(
        "Retention Policy Executed",
        `Retention policy has been executed successfully. ${results.archivedRecords || 0} records archived. State: ${state || 'All'}, Course Type: ${courseType || 'All'}.`,
        {
          archivedRecords: results.archivedRecords || 0,
          state: state || 'All',
          courseType: courseType || 'All',
          retentionDate: retentionDate,
          operation: 'execute'
        }
      );
    }
  };

  // Handle verification completion
  const handleVerificationCompleted = (results: any) => {
    // Send notification when verification completes
    if (results) {
      sendRetentionNotification(
        "Retention Verification Completed",
        `Retention verification completed with ${results.verifiedRecords || 0} records verified. State: ${state || 'All'}, Course Type: ${courseType || 'All'}.`,
        {
          verifiedRecords: results.verifiedRecords || 0,
          state: state || 'All',
          courseType: courseType || 'All',
          operation: 'verify'
        }
      );
    }
  };

  return (
    <div>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg md:text-xl">Execute Retention</CardTitle>
          <CardDescription>
            Archive certificates based on retention policies and compliance requirements
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4 md:space-y-6">
          <RetentionFilters 
            state={state}
            setState={setState}
            courseType={courseType}
            setCourseType={setCourseType}
            retentionDate={retentionDate}
            setRetentionDate={setRetentionDate}
          />
          
          {dryRunResults && <DryRunResults dryRunResults={dryRunResults} />}
        </CardContent>
        
        <CardFooter className={`${isMobile ? 'flex-col items-stretch space-y-2' : ''}`}>
          <RetentionActions 
            state={state}
            courseType={courseType}
            retentionDate={retentionDate}
            onDryRunCompleted={handleDryRunCompleted}
            onExecutionCompleted={handleExecutionCompleted}
            onVerificationCompleted={handleVerificationCompleted}
          />
        </CardFooter>
      </Card>
    </div>
  );
}
